// src/pages/RegisterForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registrarUsuario } from '../actions/UserActions';
import { obtenerDepartamentos, obtenerMunicipios } from '../actions/EntidadAction';
import { 
    TextField, 
    MenuItem, 
    CircularProgress,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    Lock,
    Visibility,
    VisibilityOff,
    Church,
    ArrowBack,
    LocationOn,
    HowToReg,
    CheckCircle
} from '@mui/icons-material';
import useStyles, { capellanesColors } from '../theme/useStyles';
import logo from '../assets/logo1.png';

// Paleta de colores Capellanes
const colors = {
    gold: '#D4AF37',
    lightGold: '#F4E4BC',
    darkGold: '#B8960C',
    black: '#1A1A1A',
    darkGrey: '#2D2D2D',
    white: '#FFFFFF',
    offWhite: '#F8F6F0',
    cream: '#FDF8E8',
    error: '#E63946',
    success: '#4CAF50'
};

// Estilos para TextField estilo Capellanes
const textFieldCapellanes = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: '#FFFFFF',
        '& fieldset': {
            borderColor: 'rgba(212, 175, 55, 0.4)',
            borderWidth: '2px',
        },
        '&:hover fieldset': {
            borderColor: '#D4AF37',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#D4AF37',
            borderWidth: '2px',
        },
        '&.Mui-error fieldset': {
            borderColor: '#E63946',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#F4E4BC',
        '&.Mui-focused': { color: '#D4AF37' },
        '&.Mui-error': { color: '#E63946' },
    },
    '& .MuiInputBase-input': { color: '#FFFFFF' },
    '& .MuiFormHelperText-root': { color: '#E63946' },
    '& .MuiSelect-icon': { color: '#D4AF37' },
    '& .MuiMenuItem-root': { color: '#1A1A1A' },
};

const RegisterForm = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        departamentoID: '',
        municipioID: '',
        password: '',
        confirmarPassword: ''
    });
    
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);
    const [loadingCatalogos, setLoadingCatalogos] = useState(true);
    const [aceptaTerminos, setAceptaTerminos] = useState(false);
    const [errores, setErrores] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showTerminos, setShowTerminos] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);

    useEffect(() => {
        const cargarCatalogos = async () => {
            try {
                setLoadingCatalogos(true);
                const departamentosResponse = await obtenerDepartamentos();
                if (departamentosResponse.status === 200) {
                    setDepartamentos(departamentosResponse.data);
                }
                const municipiosResponse = await obtenerMunicipios();
                if (municipiosResponse.status === 200) {
                    setMunicipios(municipiosResponse.data);
                }
            } catch (error) {
                console.error('Error cargando catálogos:', error);
                setErrores({ general: 'Error cargando datos. Intente recargar la página.' });
            } finally {
                setLoadingCatalogos(false);
            }
        };
        cargarCatalogos();
    }, []);

    useEffect(() => {
        if (formData.departamentoID) {
            const municipiosDelDepartamento = municipios.filter(
                m => m.departamentoID === parseInt(formData.departamentoID)
            );
            setMunicipiosFiltrados(municipiosDelDepartamento);
        } else {
            setMunicipiosFiltrados([]);
        }
        if (formData.municipioID && formData.departamentoID) {
            const municipioSeleccionado = municipios.find(m => m.municipioID === parseInt(formData.municipioID));
            if (municipioSeleccionado && municipioSeleccionado.departamentoID !== parseInt(formData.departamentoID)) {
                setFormData(prev => ({ ...prev, municipioID: '' }));
            }
        }
    }, [formData.departamentoID, municipios, formData.municipioID]);

    // Animación de confetti
    useEffect(() => {
        if (showCelebration) {
            createConfetti();
            const timer = setTimeout(() => {
                navigate('/login');
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [showCelebration, navigate]);

    const createConfetti = () => {
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const confettiColors = [colors.gold, colors.lightGold, colors.darkGold, '#FFFFFF'];

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const confettiInterval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(confettiInterval);
            }

            const particleCount = 3;

            for (let i = 0; i < particleCount; i++) {
                const confetti = document.createElement('div');
                confetti.className = 'confetti-piece';
                
                const size = randomInRange(8, 15);
                const color = confettiColors[Math.floor(Math.random() * confettiColors.length)];
                const startX = randomInRange(0, 100);
                const rotation = randomInRange(0, 360);
                const confettiDuration = randomInRange(1.5, 3);
                
                confetti.style.cssText = `
                    position: fixed;
                    width: ${size}px;
                    height: ${size}px;
                    background-color: ${color};
                    left: ${startX}%;
                    top: -20px;
                    z-index: 99999;
                    opacity: 1;
                    transform: rotate(${rotation}deg);
                    animation: confetti-fall ${confettiDuration}s linear forwards;
                    border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
                `;
                
                document.body.appendChild(confetti);
                
                setTimeout(() => {
                    confetti.remove();
                }, confettiDuration * 1000);
            }
        }, 50);

        setTimeout(() => clearInterval(confettiInterval), duration);
    };

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errores[field]) {
            setErrores(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validarFormulario = () => {
        const nuevosErrores = {};
        if (!formData.nombre.trim()) nuevosErrores.nombre = 'El nombre es requerido';
        if (!formData.apellido.trim()) nuevosErrores.apellido = 'El apellido es requerido';
        if (!formData.email.trim()) {
            nuevosErrores.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            nuevosErrores.email = 'El email no es válido';
        }
        if (!formData.telefono.trim()) {
            nuevosErrores.telefono = 'El teléfono es requerido';
        } else if (!/^\d{10}$/.test(formData.telefono.replace(/\s/g, ''))) {
            nuevosErrores.telefono = 'El teléfono debe tener 10 dígitos';
        }
        if (!formData.departamentoID) nuevosErrores.departamentoID = 'Seleccione un departamento';
        if (!formData.municipioID) nuevosErrores.municipioID = 'Seleccione un municipio';
        if (!formData.password.trim()) {
            nuevosErrores.password = 'La contraseña es requerida';
        } else if (formData.password.length < 6) {
            nuevosErrores.password = 'Mínimo 6 caracteres';
        }
        if (!formData.confirmarPassword.trim()) {
            nuevosErrores.confirmarPassword = 'Confirme la contraseña';
        } else if (formData.password !== formData.confirmarPassword) {
            nuevosErrores.confirmarPassword = 'Las contraseñas no coinciden';
        }
        if (!aceptaTerminos) nuevosErrores.terminos = 'Debe aceptar los términos';
        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;
        setLoading(true);
        try {
            const departamentoSeleccionado = departamentos.find(d => d.departamentoID === parseInt(formData.departamentoID));
            const municipioSeleccionado = municipios.find(m => m.municipioID === parseInt(formData.municipioID));
            const usuarioData = {
                entidadID: 0,
                email: formData.email,
                userName: formData.email,
                nombre: formData.nombre,
                apellido: formData.apellido,
                telefono: formData.telefono,
                departamentoID: formData.departamentoID,
                municipioID: formData.municipioID,
                departamento: departamentoSeleccionado?.nombre || '',
                municipio: municipioSeleccionado?.nombre || '',
                password: formData.password,
                rol: 'user',
                imagen: ''
            };
            const response = await registrarUsuario(usuarioData);
            if (response.status === 200 || response.status === 201) {
                setShowCelebration(true);
            } else {
                setErrores({ general: response.data?.message || 'Hubo un problema al crear el usuario' });
            }
        } catch (error) {
            setErrores({ general: 'Error de conexión. Intente nuevamente.' });
        } finally {
            setLoading(false);
        }
    };

    if (loadingCatalogos) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGrey} 50%, #3D3D3D 100%)`
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} sx={{ color: colors.gold }} />
                    <Typography sx={{ mt: 2, color: colors.lightGold }}>Cargando formulario...</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGrey} 50%, #3D3D3D 100%)`,
            position: 'relative',
            overflow: 'hidden',
            py: 4,
            px: 2
        }}>
            {/* Overlay decorativo */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `radial-gradient(circle at 20% 80%, rgba(212, 175, 55, 0.15) 0%, transparent 50%), 
                                  radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 40%)`,
                pointerEvents: 'none'
            }} />

            {/* Círculos decorativos */}
            <Box sx={{
                position: 'absolute',
                top: '10%',
                left: '5%',
                width: '150px',
                height: '150px',
                border: `1px solid ${colors.gold}20`,
                borderRadius: '50%',
                animation: 'float 8s ease-in-out infinite'
            }} />
            <Box sx={{
                position: 'absolute',
                bottom: '15%',
                right: '10%',
                width: '100px',
                height: '100px',
                border: `1px solid ${colors.gold}30`,
                borderRadius: '50%',
                animation: 'float 6s ease-in-out infinite 1s'
            }} />

            {/* Card principal */}
            <Box sx={{
                width: '100%',
                maxWidth: '1100px',
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5)',
                position: 'relative',
                zIndex: 1
            }}>
                {/* Panel izquierdo - Info */}
                <Box sx={{
                    flex: { xs: 'none', lg: '0 0 40%' },
                    background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.darkGold} 100%)`,
                    p: { xs: 4, md: 5 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decoración */}
                    <Box sx={{
                        position: 'absolute',
                        top: '-50px',
                        right: '-50px',
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)'
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        bottom: '-30px',
                        left: '-30px',
                        width: '150px',
                        height: '150px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.08)'
                    }} />

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {/* Logo */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="Logo Alianza de Capellanes"
                                sx={{
                                    width: 100,
                                    height: 'auto',
                                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                                }}
                            />
                        </Box>

                        <Typography variant="h4" sx={{
                            fontWeight: 800,
                            color: colors.black,
                            textAlign: 'center',
                            mb: 2,
                            textShadow: '1px 1px 2px rgba(255,255,255,0.3)'
                        }}>
                            ¡Únete a la Alianza!
                        </Typography>

                        <Typography sx={{
                            color: colors.darkGrey,
                            textAlign: 'center',
                            mb: 4,
                            fontSize: '1rem',
                            lineHeight: 1.6
                        }}>
                            Forma parte de la Alianza Solidaria de Capellanes de América Latina y contribuye a llevar esperanza y servicio a quienes más lo necesitan.
                        </Typography>

                        {/* Beneficios */}
                        <Box sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 3,
                            p: 3,
                            backdropFilter: 'blur(10px)'
                        }}>
                            <Typography sx={{
                                fontWeight: 700,
                                color: colors.black,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Church sx={{ fontSize: 24 }} />
                                Beneficios de ser miembro:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {[
                                    'Acceso a formación y capacitación',
                                    'Red de apoyo internacional',
                                    'Certificación como capellán',
                                    'Recursos y materiales exclusivos',
                                    'Participación en eventos y congresos'
                                ].map((item, idx) => (
                                    <Box component="li" key={idx} sx={{
                                        color: colors.darkGrey,
                                        py: 0.5,
                                        fontSize: '0.9rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <CheckCircle sx={{ fontSize: 16, color: colors.black }} />
                                        {item}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Panel derecho - Formulario */}
                <Box sx={{
                    flex: 1,
                    bgcolor: colors.darkGrey,
                    p: { xs: 3, md: 4 },
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Typography variant="h5" sx={{
                        color: colors.gold,
                        fontWeight: 700,
                        textAlign: 'center',
                        mb: 1
                    }}>
                        Crear Cuenta
                    </Typography>
                    <Typography sx={{
                        color: colors.lightGold,
                        textAlign: 'center',
                        mb: 3,
                        fontSize: '0.9rem'
                    }}>
                        Complete el formulario para registrarse
                    </Typography>

                    {/* Error general */}
                    {errores.general && (
                        <Box sx={{
                            p: 2,
                            mb: 2,
                            bgcolor: 'rgba(230, 57, 70, 0.15)',
                            border: `1px solid ${colors.error}`,
                            borderRadius: 2
                        }}>
                            <Typography sx={{ color: colors.error, fontSize: '0.9rem' }}>
                                {errores.general}
                            </Typography>
                        </Box>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Nombre y Apellido */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Nombre"
                                variant="outlined"
                                size="small"
                                value={formData.nombre}
                                onChange={handleChange('nombre')}
                                error={!!errores.nombre}
                                helperText={errores.nombre}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person sx={{ color: colors.gold, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldCapellanes}
                            />
                            <TextField
                                fullWidth
                                label="Apellido"
                                variant="outlined"
                                size="small"
                                value={formData.apellido}
                                onChange={handleChange('apellido')}
                                error={!!errores.apellido}
                                helperText={errores.apellido}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person sx={{ color: colors.gold, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldCapellanes}
                            />
                        </Box>

                        {/* Email */}
                        <TextField
                            fullWidth
                            label="Correo Electrónico"
                            type="email"
                            variant="outlined"
                            size="small"
                            value={formData.email}
                            onChange={handleChange('email')}
                            error={!!errores.email}
                            helperText={errores.email}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: colors.gold, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2, ...textFieldCapellanes }}
                        />

                        {/* Teléfono */}
                        <TextField
                            fullWidth
                            label="Teléfono"
                            type="tel"
                            variant="outlined"
                            size="small"
                            value={formData.telefono}
                            onChange={handleChange('telefono')}
                            error={!!errores.telefono}
                            helperText={errores.telefono}
                            placeholder="3001234567"
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Phone sx={{ color: colors.gold, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2, ...textFieldCapellanes }}
                        />

                        {/* Departamento y Municipio */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                select
                                label="Departamento"
                                variant="outlined"
                                size="small"
                                value={formData.departamentoID}
                                onChange={(e) => setFormData({...formData, departamentoID: e.target.value, municipioID: ''})}
                                error={!!errores.departamentoID}
                                helperText={errores.departamentoID}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationOn sx={{ color: colors.gold, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldCapellanes}
                            >
                                <MenuItem value=""><em>Seleccione...</em></MenuItem>
                                {departamentos.map((departamento) => (
                                    <MenuItem key={departamento.departamentoID} value={departamento.departamentoID}>
                                        {departamento.nombre}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                select
                                label="Municipio"
                                variant="outlined"
                                size="small"
                                value={formData.municipioID}
                                onChange={handleChange('municipioID')}
                                error={!!errores.municipioID}
                                helperText={errores.municipioID || (!formData.departamentoID ? 'Primero seleccione departamento' : '')}
                                disabled={loading || !formData.departamentoID}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationOn sx={{ color: colors.gold, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldCapellanes}
                            >
                                <MenuItem value=""><em>Seleccione...</em></MenuItem>
                                {municipiosFiltrados.map((municipio) => (
                                    <MenuItem key={municipio.municipioID} value={municipio.municipioID}>
                                        {municipio.nombre}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        {/* Contraseña */}
                        <TextField
                            fullWidth
                            label="Contraseña"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            size="small"
                            value={formData.password}
                            onChange={handleChange('password')}
                            error={!!errores.password}
                            helperText={errores.password}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: colors.gold, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{ color: colors.lightGold }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2, ...textFieldCapellanes }}
                        />

                        {/* Confirmar Contraseña */}
                        <TextField
                            fullWidth
                            label="Confirmar Contraseña"
                            type={showConfirmPassword ? 'text' : 'password'}
                            variant="outlined"
                            size="small"
                            value={formData.confirmarPassword}
                            onChange={handleChange('confirmarPassword')}
                            error={!!errores.confirmarPassword}
                            helperText={errores.confirmarPassword}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: colors.gold, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            sx={{ color: colors.lightGold }}
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2, ...textFieldCapellanes }}
                        />

                        {/* Términos y condiciones */}
                        <Box sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: 'rgba(212, 175, 55, 0.1)',
                            borderRadius: 2,
                            border: errores.terminos ? `2px solid ${colors.error}` : `1px solid ${colors.gold}30`
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={aceptaTerminos}
                                        onChange={(e) => {
                                            setAceptaTerminos(e.target.checked);
                                            if (errores.terminos) setErrores({ ...errores, terminos: '' });
                                        }}
                                        disabled={loading}
                                        sx={{
                                            color: colors.gold,
                                            '&.Mui-checked': { color: colors.gold }
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: colors.lightGold, fontSize: '0.85rem' }}>
                                        Acepto los{' '}
                                        <Box
                                            component="span"
                                            onClick={(e) => { e.preventDefault(); setShowTerminos(true); }}
                                            sx={{
                                                color: colors.gold,
                                                fontWeight: 600,
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                '&:hover': { color: colors.lightGold }
                                            }}
                                        >
                                            términos y condiciones
                                        </Box>
                                        {' '}y la política de privacidad
                                    </Typography>
                                }
                            />
                            {errores.terminos && (
                                <Typography sx={{ color: colors.error, fontSize: '0.75rem', ml: 4 }}>
                                    {errores.terminos}
                                </Typography>
                            )}
                        </Box>

                        {/* Botón de Registro */}
                        <Box
                            component="button"
                            type="submit"
                            disabled={loading}
                            sx={{
                                width: '100%',
                                py: 1.5,
                                px: 4,
                                bgcolor: colors.gold,
                                color: colors.black,
                                border: 'none',
                                borderRadius: 2,
                                fontSize: '1rem',
                                fontWeight: 700,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                opacity: loading ? 0.7 : 1,
                                '&:hover': {
                                    bgcolor: loading ? colors.gold : colors.darkGold,
                                    transform: loading ? 'none' : 'translateY(-2px)',
                                    boxShadow: loading ? '0 4px 15px rgba(212, 175, 55, 0.4)' : '0 6px 20px rgba(212, 175, 55, 0.5)'
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: colors.black }} />
                                    Registrando...
                                </>
                            ) : (
                                <>
                                    <HowToReg sx={{ fontSize: 20 }} />
                                    Unirme a la Alianza
                                </>
                            )}
                        </Box>

                        {/* Divider */}
                        <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: `${colors.gold}40` }} />
                            <Typography sx={{ px: 2, color: colors.lightGold, fontSize: '0.85rem' }}>
                                ¿Ya tienes cuenta?
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: `${colors.gold}40` }} />
                        </Box>

                        {/* Botón de Login */}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => navigate('/login')}
                            sx={{
                                width: '100%',
                                py: 1.5,
                                px: 4,
                                bgcolor: 'transparent',
                                color: colors.gold,
                                border: `2px solid ${colors.gold}`,
                                borderRadius: 2,
                                fontSize: '1rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    bgcolor: `${colors.gold}15`,
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            Iniciar Sesión
                        </Box>

                        {/* Volver al inicio */}
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Box
                                component="span"
                                onClick={() => navigate('/')}
                                sx={{
                                    color: colors.lightGold,
                                    fontSize: '0.9rem',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        color: colors.gold
                                    }
                                }}
                            >
                                <ArrowBack sx={{ fontSize: 18 }} />
                                Volver al inicio
                            </Box>
                        </Box>
                    </form>
                </Box>
            </Box>

            {/* Modal de Términos y Condiciones */}
            {showTerminos && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    p: 2
                }} onClick={() => setShowTerminos(false)}>
                    <Box sx={{
                        bgcolor: 'transparent',
                        borderRadius: 4,
                        maxWidth: '950px',
                        width: '100%',
                        height: '90vh',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column'
                    }} onClick={(e) => e.stopPropagation()}>
                        
                        {/* Botón cerrar */}
                        <Box
                            component="button"
                            onClick={() => setShowTerminos(false)}
                            sx={{
                                position: 'absolute',
                                top: '-20px',
                                right: '-20px',
                                bgcolor: colors.error,
                                border: 'none',
                                borderRadius: '50%',
                                width: '50px',
                                height: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '28px',
                                color: 'white',
                                zIndex: 10001,
                                boxShadow: `0 4px 12px rgba(230, 57, 70, 0.5)`,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    bgcolor: '#C62828'
                                }
                            }}
                        >
                            ×
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            borderRadius: 4,
                            overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            bgcolor: 'white'
                        }}>
                            <Box
                                component="iframe"
                                src="/terminos-y-condiciones-iframe.html"
                                title="Términos y Condiciones"
                                sx={{
                                    width: '100%',
                                    flex: 1,
                                    border: 'none',
                                    bgcolor: 'white'
                                }}
                            />

                            <Box sx={{
                                p: 2,
                                borderTop: `2px solid ${colors.gold}30`,
                                bgcolor: colors.cream
                            }}>
                                <Box
                                    component="button"
                                    onClick={() => setShowTerminos(false)}
                                    sx={{
                                        width: '100%',
                                        py: 1.5,
                                        bgcolor: colors.gold,
                                        color: colors.black,
                                        border: 'none',
                                        borderRadius: 2,
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            bgcolor: colors.darkGold
                                        }
                                    }}
                                >
                                    <CheckCircle sx={{ fontSize: 20 }} />
                                    He leído y acepto los términos y condiciones
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Modal de Celebración */}
            {showCelebration && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 99998,
                    animation: 'fadeIn 0.3s ease-in'
                }}>
                    <Box sx={{
                        bgcolor: colors.darkGrey,
                        borderRadius: 4,
                        p: { xs: 4, md: 6 },
                        maxWidth: '550px',
                        textAlign: 'center',
                        boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 3px ${colors.gold}`,
                        animation: 'scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                    }}>
                        <Box sx={{
                            fontSize: '80px',
                            mb: 2,
                            animation: 'bounce 1s infinite'
                        }}>
                            🎉
                        </Box>
                        <Typography variant="h4" sx={{
                            fontWeight: 800,
                            color: colors.gold,
                            mb: 2
                        }}>
                            ¡Bienvenido a la Alianza!
                        </Typography>
                        <Typography sx={{
                            color: colors.lightGold,
                            mb: 3,
                            fontSize: '1.1rem'
                        }}>
                            Tu cuenta ha sido creada exitosamente
                        </Typography>
                        
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: `${colors.gold}20`,
                            border: `2px solid ${colors.gold}`,
                            borderRadius: 3,
                            py: 1.5,
                            px: 3,
                            mb: 3
                        }}>
                            <Church sx={{ fontSize: 28, color: colors.gold }} />
                            <Typography sx={{ color: colors.gold, fontWeight: 700 }}>
                                Ya eres parte de los Capellanes
                            </Typography>
                        </Box>

                        {/* Mensaje de confirmación por correo */}
                        <Box sx={{
                            bgcolor: 'rgba(212, 175, 55, 0.1)',
                            border: `1px solid ${colors.gold}50`,
                            borderRadius: 2,
                            p: 2,
                            mt: 2,
                            textAlign: 'left'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Email sx={{ fontSize: 28, color: colors.gold, flexShrink: 0 }} />
                                <Box>
                                    <Typography sx={{
                                        color: colors.gold,
                                        fontWeight: 600,
                                        mb: 0.5
                                    }}>
                                        Paso importante:
                                    </Typography>
                                    <Typography sx={{
                                        color: colors.lightGold,
                                        fontSize: '0.9rem',
                                        lineHeight: 1.5
                                    }}>
                                        Revisa tu correo electrónico y haz clic en el botón de confirmación para <strong>activar tu cuenta</strong>.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Typography sx={{
                            color: 'rgba(255,255,255,0.5)',
                            mt: 3,
                            fontSize: '0.9rem'
                        }}>
                            Redirigiendo al login...
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* CSS Animations */}
            <style>
                {`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-15px); }
                    }
                    @keyframes confetti-fall {
                        0% {
                            transform: translateY(-20px) rotateZ(0deg);
                            opacity: 1;
                        }
                        100% {
                            transform: translateY(100vh) rotateZ(720deg);
                            opacity: 0;
                        }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes scaleIn {
                        0% {
                            transform: scale(0.5);
                            opacity: 0;
                        }
                        100% {
                            transform: scale(1);
                            opacity: 1;
                        }
                    }
                    @keyframes bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-20px); }
                    }
                `}
            </style>
        </Box>
    );
};

export default RegisterForm;